import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiWifi, FiWifiOff, FiUsers, FiMonitor, FiChevronDown, FiChevronUp } = FiIcons;

const ConnectionStatus = ({ isConnected, userCount = 0, deviceId, connectedDevices = [] }) => {
  const [showDevices, setShowDevices] = React.useState(false);

  return (
    <div className="space-y-2">
      <motion.div 
        className="flex items-center gap-4 text-sm" 
        initial={{ opacity: 0, y: -10 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center gap-2">
          <motion.div
            animate={{
              scale: isConnected ? [1, 1.2, 1] : 1,
              rotate: isConnected ? 0 : [0, -10, 10, 0]
            }}
            transition={{ 
              duration: isConnected ? 2 : 0.5, 
              repeat: isConnected ? Infinity : 0 
            }}
          >
            <SafeIcon 
              icon={isConnected ? FiWifi : FiWifiOff} 
              className={`w-4 h-4 ${isConnected ? 'text-success-500' : 'text-danger-500'}`} 
            />
          </motion.div>
          <span className={`font-medium ${isConnected ? 'text-success-600' : 'text-danger-600'}`}>
            {isConnected ? 'Connected' : 'Disconnected'}
          </span>
          
          {/* Display current device ID in green */}
          {isConnected && deviceId && (
            <span className="ml-1 text-green-600 font-mono text-xs bg-green-50 px-2 py-0.5 rounded-full">
              {deviceId}
            </span>
          )}
        </div>
        
        {isConnected && (
          <motion.div 
            className="flex items-center gap-2 text-gray-600" 
            initial={{ opacity: 0, scale: 0.8 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ delay: 0.2 }}
          >
            <SafeIcon icon={FiUsers} className="w-4 h-4" />
            <span>
              {userCount} {userCount === 1 ? 'user' : 'users'} online
            </span>
            
            {/* Toggle button for device list */}
            {connectedDevices.length > 0 && (
              <button 
                onClick={() => setShowDevices(!showDevices)}
                className="ml-1 p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <SafeIcon 
                  icon={showDevices ? FiChevronUp : FiChevronDown} 
                  className="w-4 h-4 text-gray-500" 
                />
              </button>
            )}
          </motion.div>
        )}
      </motion.div>
      
      {/* Connected devices list */}
      <AnimatePresence>
        {isConnected && showDevices && connectedDevices.length > 0 && (
          <motion.div 
            className="bg-gray-50 rounded-lg p-3 text-sm"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="mb-2 text-xs font-medium text-gray-500">Connected Devices</div>
            <div className="space-y-2">
              {connectedDevices.map((device, index) => (
                <div key={index} className="flex items-center gap-2">
                  <SafeIcon icon={FiMonitor} className="w-3.5 h-3.5 text-gray-400" />
                  <span className="font-mono text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                    {device.deviceId || device.id || `Device-${index}`}
                  </span>
                  {device.ip && (
                    <span className="text-xs text-gray-500">
                      ({device.ip})
                    </span>
                  )}
                  {device.isCurrent && (
                    <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full">
                      You
                    </span>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConnectionStatus;